import { z } from "zod";
import { etatEnvoiSchema, type Reseau, reseauSchema } from "./reseaux";
import { modeRobotSchema } from "./robot";

/**
 * Le contrat de la diffusion, partage entre le backend (`packages/backend/src/app/diffusion/`) et la
 * regie (l'ecran de la semaine, du stock et des reglages). Les deux cotes le lisent par leur pont
 * `src/app/contract.ts`.
 *
 * Il vit ici, et non dans `contract.ts` de ce paquet, parce que `src/contract.ts` appartient au
 * template : l'app possede `src/app/**`. Il dit ce que l'agent du PC envoie de son inventaire, la
 * fiche d'un clip, les reglages de la diffusion, puis la semaine (ses longueurs, sa vue, ce que la
 * preparation, la validation et la modification d'un post rendent) et la reponse du stock, en fin de
 * fichier.
 *
 * Les bornes de ce qui est recu sont celles que la base sait ranger (un `integer`, un `numeric(7,2)`,
 * une date qu'elle lit), et ce qu'un nom de fichier peut etre. Elles laissent loin derriere elles un
 * inventaire reel (229 fichiers, 460 fiches, 512 Kio au plus par la route qui le recoit) : aucun clip
 * incomplet ne doit faire refuser tout l'inventaire, mais une valeur que la base refuserait ferait
 * echouer l'application de tout l'inventaire, et mieux vaut la refuser tout de suite, par son nom.
 */

/** Combien de fichiers un inventaire porte au plus : le dossier du stock en a 229. */
export const INVENTAIRE_FICHIERS_MAX = 2000;
/** Combien de fiches la liste d'un inventaire porte au plus : celle du kit en a 460. */
export const INVENTAIRE_FICHES_MAX = 5000;

/** Un nom de fichier du dossier : 255 caracteres au plus (la limite de NTFS), jamais un chemin. */
const NOM_MAX = 255;
/** L'identifiant d'un clip Twitch, son titre et le nom de son jeu : bien plus que ce que Twitch ecrit. */
const IDENTIFIANT_MAX = 200;
const TITRE_MAX = 300;
const JEU_MAX = 200;
/**
 * Combien de points de code une accroche compte au plus (le `liste-verticales.csv` du stock n'en a pas de plus
 * longue que 35). **Elle doit rester egale a `ACCROCHE_MAX` de `packages/atelier/atelier/inventaire.py`** : l'agent
 * n'envoie pas une accroche plus longue, et la sortie est lue en bloc, un test de l'agent lit cette ligne. Comptee en
 * points de code et non en unites UTF-16 comme le fait `.max()` de zod, pour que les deux cotes mesurent pareil.
 */
export const ACCROCHE_MAX = 200;
/** La colonne `duree_s` est un `numeric(7,2)` : 99 999,99 au plus. */
const DUREE_MAX = 99_999;
/** La colonne `vues_twitch` est un `integer`. */
const VUES_MAX = 2_147_483_647;
/**
 * Aucun clip n'est d'avant 2000 (Twitch est ne en 2011) : une date plus ancienne est fausse. La borne
 * ecarte surtout l'an zero, que Postgres refuse et que `z.iso.datetime` laisse passer.
 */
const PREMIER_INSTANT = Date.UTC(2000, 0, 1);

/** Un nom de fichier : ni caractere de controle (NUL, saut de ligne), ni separateur de chemin. */
const UN_NOM = /^[^\p{Cc}/\\]+$/u;
/** Un texte qui n'est pas vide et sans caractere de controle : un identifiant de clip, une accroche. */
const SANS_CONTROLE = /^[^\p{Cc}]+$/u;
/** L'empreinte d'un fichier : un SHA-256 en 64 chiffres hexadecimaux minuscules, comme `fichiers.empreinte`. */
const UNE_EMPREINTE = /^[0-9a-f]{64}$/;
/**
 * Un jour : AAAA-MM-JJ, en chiffres ASCII (le `\d` de JavaScript n'en connait pas d'autres, et son `$` n'admet pas un
 * saut de ligne derriere), sans rien avant ni apres. **Meme forme que `_JOUR` de `packages/atelier/atelier/inventaire.py`**.
 */
const UN_JOUR = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Vrai pour un texte AAAA-MM-JJ qui est un jour du calendrier, de l'an 1 (le type `date` de Postgres n'a pas d'an zero) a
 * l'an 9999 : un 30 fevrier ou un mois 13 n'en sont pas. **Meme regle que `valider_publication_prevue` de
 * `packages/atelier/atelier/inventaire.py`**, qui n'envoie que des jours que celle-ci lit : leur table de cas (les jours
 * acceptes et refuses) est un seul fichier, `test/app-jours-prevus.json`, que lisent `test/app-diffusion.test.ts` et le
 * `tests/test_inventaire.py` de l'agent. Un jour que l'agent enverrait et que ce schema refuserait ferait refuser tout
 * l'inventaire.
 */
function estUnJourDuCalendrier(texte: string): boolean {
  if (!UN_JOUR.test(texte)) {
    return false;
  }
  const annee = Number(texte.slice(0, 4));
  if (annee < 1) {
    return false;
  }
  // `setUTCFullYear` et non `Date.UTC` : celui-ci lit une annee de 0 a 99 comme 1900 a 1999.
  const date = new Date(0);
  date.setUTCFullYear(annee, Number(texte.slice(5, 7)) - 1, Number(texte.slice(8, 10)));
  // Un 30 fevrier ou un mois 13 debordent sur le mois d'apres : la date relue n'est plus celle qu'on a ecrite.
  return date.toISOString().slice(0, 10) === texte;
}

/** Une duree en secondes, que la colonne `duree_s` sait ranger. */
const duree = z.number().min(0).max(DUREE_MAX);

/**
 * Un fichier du dossier du stock, tel que `packages/atelier/atelier/inventaire.py` le decrit. `nom` est
 * le nom brut du dossier, accents compris, et non le nom nettoye que porte le fichier en base : la tache
 * de televersement nomme le clip par ce nom brut. `dureeS` est `null` quand la duree du fichier ne se lit
 * pas (un MP4 fragmente, un fichier coupe).
 *
 * `accroche` est la phrase ecrite en majuscules sur la video pendant ses premieres secondes : l'agent la lit dans le
 * `liste-verticales.csv` du dossier et la nettoie (`nettoyer_accroche`). Elle manque, ou vaut `null`, quand le fichier
 * n'en a pas : la sortie d'un agent qui n'en envoie pas encore reste valide. Le schema refuse un texte vide, trop
 * long ou qui porte un caractere de controle, que l'agent n'envoie jamais (il le nettoie ou l'ecarte) : comme la
 * sortie est lue en bloc, c'est alors tout l'inventaire qui est refuse. Ne sert qu'a l'IA des textes, pas a la regie.
 *
 * `publication_prevue` est le jour de sortie que le `liste-verticales.csv` prevoit pour le clip (sa colonne du meme nom),
 * que l'agent envoie tel quel : exactement AAAA-MM-JJ, et une vraie date du calendrier (`estUnJourDuCalendrier`). Elle
 * manque, ou vaut `null`, quand le fichier n'en a pas (le CSV n'a pas la colonne, la case est vide ou ne se lit pas) : la
 * sortie d'un agent qui n'en envoie pas encore reste valide, et un Castellan qui ne la connait pas encore la retire en
 * lisant (zod ignore une cle inconnue) : le deploiement se fait dans l'ordre qu'on veut. Comme l'accroche, une valeur
 * fausse fait refuser tout l'inventaire (la sortie est lue en bloc), ce que l'agent ne provoque jamais : il ecarte tout ce
 * qui n'est pas un jour. La preparation de la semaine la suit (`preparation.ts`) : le clip sort ce jour-la.
 */
const fichierInventaireSchema = z.object({
  nom: z.string().min(1).max(NOM_MAX).regex(UN_NOM),
  taille: z.number().int().min(0),
  dureeS: duree.nullish(),
  empreinte: z.string().regex(UNE_EMPREINTE),
  accroche: z
    .string()
    .min(1)
    .regex(SANS_CONTROLE)
    .refine((texte) => Array.from(texte).length <= ACCROCHE_MAX, {
      message: `Une accroche compte ${ACCROCHE_MAX} points de code au plus.`,
    })
    .nullish(),
  publication_prevue: z
    .string()
    .regex(UN_JOUR)
    .refine(estUnJourDuCalendrier, { message: "Un jour AAAA-MM-JJ du calendrier est attendu." })
    .nullish(),
});

/**
 * Une fiche de la liste des clips Twitch. L'agent ne garde que six champs, et seulement ceux que le clip
 * a : une fiche sans jeu ou sans duree arrive sans eux, et un champ peut aussi valoir `null`. Seul `id`
 * est exige. Les autres champs du clip (le nom du clippeur, la chaine, les adresses) ne quittent jamais
 * le PC ; et si l'un d'eux arrivait, le schema le retire, comme tout champ qu'il ne connait pas.
 */
const ficheListeSchema = z.object({
  id: z.string().min(1).max(IDENTIFIANT_MAX).regex(SANS_CONTROLE),
  title: z.string().max(TITRE_MAX).nullish(),
  game: z.string().max(JEU_MAX).nullish(),
  created_at: z.iso
    .datetime({ offset: true })
    .refine((date) => Date.parse(date) >= PREMIER_INSTANT, {
      message: "Aucun clip n'est d'avant l'an 2000.",
    })
    .nullish(),
  view_count: z.number().int().min(0).max(VUES_MAX).nullish(),
  duration: duree.nullish(),
});

/**
 * La sortie de la tache `inventaire` de l'agent du PC : les fichiers du dossier du stock, et la liste des
 * clips Twitch. La sortie est lue en bloc : une seule valeur fausse la fait refuser en entier.
 */
export const sortieInventaireSchema = z.object({
  fichiers: z.array(fichierInventaireSchema).max(INVENTAIRE_FICHIERS_MAX),
  liste: z.array(ficheListeSchema).max(INVENTAIRE_FICHES_MAX),
});
export type SortieInventaire = z.infer<typeof sortieInventaireSchema>;

/**
 * La fiche d'un clip du stock, telle que la regie la lit.
 *
 * - `origine` : `stock` (les clips montes par la session Cowork) ou `ecuyer` (un depot de L'Ecuyer) ;
 * - `cleOrigine` : l'identifiant du clip Twitch, ou `fichier:<empreinte>` pour un fichier sans fiche ;
 * - `nomFichier`, `empreinte` : le fichier du dossier, tel que le dernier inventaire l'a vu ;
 * - `dureeS` : la duree du fichier, sinon celle du clip Twitch ; `null` si aucune n'est connue ;
 * - `jeu`, `titreTwitch`, `vuesTwitch`, `dateClip` : ce que la liste en dit, `null` pour un fichier sans
 *   fiche (le jeu est inconnu). `dateClip` est une date ISO 8601 (UTC) ;
 * - `present` : le fichier est dans le dossier du stock, d'apres le dernier inventaire ;
 * - `fichierId` : le fichier envoye a Castellan (`fichiers`), `null` tant qu'il ne l'est pas ou une fois
 *   purge ;
 * - `publicationPrevue` : le jour (AAAA-MM-JJ, jour de Paris) ou le `liste-verticales.csv` prevoit la sortie du clip,
 *   `null` quand il n'en dit rien (un clip sans date, un fichier sans fiche). La preparation de la semaine prend un clip
 *   le jour qu'il annonce, et jamais un autre ; un jour deja passe n'est plus pris par aucune semaine ;
 * - `publie` : le clip est dans un post non retire d'une semaine validee, donc plus a prendre.
 *
 * L'accroche du clip (`ClipStock.accroche` du backend, la colonne `diffusion_clips.accroche`) n'y est pas : elle ne sert
 * qu'a l'IA des textes. Les routes passent par ce schema, qui retire tout champ qu'il ne connait pas : la regie ne la lit
 * pas.
 */
export const clipStockSchema = z.object({
  id: z.string(),
  origine: z.enum(["stock", "ecuyer"]),
  cleOrigine: z.string(),
  nomFichier: z.string().nullable(),
  empreinte: z.string().nullable(),
  dureeS: z.number().nullable(),
  jeu: z.string().nullable(),
  titreTwitch: z.string().nullable(),
  vuesTwitch: z.number().int().nullable(),
  dateClip: z.string().nullable(),
  publicationPrevue: z.string().nullable(),
  present: z.boolean(),
  fichierId: z.string().nullable(),
  publie: z.boolean(),
});
export type ClipStockContrat = z.infer<typeof clipStockSchema>;

// --- Les reglages ---------------------------------------------------------------------------------

/**
 * Les modeles de l'API d'Anthropic que Nicolas peut regler pour ecrire les textes : Opus 5.5 par defaut,
 * comme le veut le guide de l'API, puis Sonnet 5.5 et Haiku 5.5. C'est la liste de la contrainte de la
 * base (`diffusion_reglages_modele_check`), des prix et du repli de `textes.ts`, et la regie en fait son
 * choix. Elle n'est ecrite qu'ici : `textes.ts` la lit par le pont `contract.ts`.
 */
export const MODELES = ["claude-opus-5-5", "claude-sonnet-5-5", "claude-haiku-5-5"] as const;
export type Modele = (typeof MODELES)[number];

/** Le ton et les exemples de posts : 4 000 caracteres au plus chacun, de quoi tenir dans la consigne. */
export const REGLAGES_TEXTE_MAX = 4000;

/** Un jour ISO : 1 pour le lundi, 7 pour le dimanche. */
const jourIsoSchema = z.number().int().min(1).max(7);

/**
 * Une heure HH:MM, de 00:00 a 23:59, en chiffres ASCII (le `\d` de JavaScript n'en connait pas d'autres, et son
 * `$` n'admet pas un saut de ligne derriere). C'est la forme que lit `momentParis` (`diffusion/heures.ts`) : le
 * test des reglages verifie que les deux disent oui et non aux memes textes. La base, elle, laisse passer
 * 24:00 a 29:59 : ses bornes sont celles-ci.
 */
const UNE_HEURE = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const heureSchema = z.string().regex(UNE_HEURE, "Une heure HH:MM de 00:00 a 23:59 est attendue.");

/** Vrai quand aucune valeur ne revient deux fois. */
const sansDoublon = (valeurs: readonly unknown[]): boolean =>
  new Set(valeurs).size === valeurs.length;

/** Le texte d'un NUL, que Postgres ne sait pas ranger dans un texte. */
const NUL = String.fromCodePoint(0);

/** Un texte libre des reglages (le ton, les exemples) : 4 000 caracteres au plus, sans NUL. */
const texteLibreSchema = z
  .string()
  .max(REGLAGES_TEXTE_MAX)
  .refine((texte) => !texte.includes(NUL), { message: "Un texte ne peut pas contenir de NUL." });

/**
 * Les reglages de la diffusion, la ligne unique de `diffusion_reglages` (`1006_diffusion.sql`) telle que la
 * regie la lit et l'ecrit (`GET` et `PUT /api/diffusion/reglages`) et que la preparation de la semaine la lit.
 *
 * - `jours` : les jours de diffusion, en jours ISO (1 pour le lundi, 7 pour le dimanche), au moins un, chacun
 *   une seule fois. L'ordre n'y compte pas : la preparation les prend dans l'ordre de la semaine ;
 * - `heures` : l'heure de Paris du creneau de chaque reseau, `HH:MM`, pour les sept reseaux, ni plus ni moins ;
 * - `reseauxStock` : les reseaux coches d'office sur un post tire du stock, au moins un, chacun une seule fois
 *   (Nicolas decoche ensuite, post par post, ceux qu'il ne veut pas) ;
 * - `ton`, `exemples` : ce que Nicolas dit du ton de ses posts et quelques posts qui ont marche, passes tels
 *   quels a la consigne des textes (`REGLAGES_TEXTE_MAX` caracteres au plus chacun, comptes comme le fait
 *   JavaScript) ;
 * - `modele` : le modele qui ecrit les textes (`MODELES`) ;
 * - `preparationJour`, `preparationHeure` : le jour ISO et l'heure de Paris ou Castellan prepare la semaine
 *   suivante (le dimanche a 09:00 de naissance).
 *
 * Le schema ne transforme rien et ne retire que les champs qu'il ne connait pas : ce que la regie envoie est
 * ce qui est range, et la regie relit ce qui est range.
 */
export const reglagesSchema = z.object({
  jours: z
    .array(jourIsoSchema)
    .min(1)
    .refine(sansDoublon, { message: "Un jour ne peut figurer qu'une fois." }),
  heures: z.record(reseauSchema, heureSchema),
  reseauxStock: z
    .array(reseauSchema)
    .min(1)
    .refine(sansDoublon, { message: "Un reseau ne peut figurer qu'une fois." }),
  ton: texteLibreSchema,
  exemples: texteLibreSchema,
  modele: z.enum(MODELES),
  preparationJour: jourIsoSchema,
  preparationHeure: heureSchema,
  /**
   * Le mode du robot de TikTok et l'heure de son passage quotidien (lot M3). Facultatifs a l'ecriture : une regie qui ne les
   * connait pas garde ceux qui sont en base (`arret`, `10:00` par defaut) ; la lecture les rend toujours.
   */
  robotMode: modeRobotSchema.optional(),
  robotHeure: heureSchema.optional(),
});
export type ReglagesContrat = z.infer<typeof reglagesSchema>;

// --- La semaine -----------------------------------------------------------------------------------

/**
 * Les longueurs maximales de chaque reseau, en caracteres tels que l'oeil les compte (graphemes). Seul
 * YouTube a un titre, en plus du texte (sa description). Celles de la spec de la diffusion.
 *
 * Elles vivent ici parce que trois lecteurs en ont besoin : la consigne les dit au modele
 * (`consigne.ts`), `textes.ts` et `semaine.ts` les verifient apres coup, et la regie en fait les
 * compteurs de chaque champ de texte. **L'ordre des cles compte** : `textes.ts` met les reseaux dans
 * cet ordre pour le schema et le message envoyes au modele, le meme d'un appel a l'autre.
 */
export const LONGUEURS: Readonly<
  Record<Reseau, { readonly titre?: number; readonly texte: number }>
> = {
  youtube: { titre: 100, texte: 1000 },
  bluesky: { texte: 300 },
  threads: { texte: 500 },
  x: { texte: 280 },
  instagram: { texte: 2200 },
  facebook: { texte: 2200 },
  tiktok: { texte: 2200 },
};

/**
 * Un identifiant de la base (un post, un clip) tel que l'API le rend : un entier positif ecrit en texte, sans
 * zero en tete, 18 chiffres au plus (il tient dans un `bigint`). Ce qui n'a pas cette forme n'est l'identifiant
 * de rien : la route le refuse avant la base, que Postgres ferait lever sur un texte qui n'est pas un entier.
 */
export const identifiantSchema = z.string().regex(/^[1-9][0-9]{0,17}$/);

/**
 * L'etat de l'ecriture des textes d'un post par l'IA : ceux de la contrainte de `diffusion_posts.ia`
 * (`1006_diffusion.sql`). `a_ecrire` : le fond ecrit ; `ecrits` : fait ; `echec`, `plafond` et `refus` : il n'a pas
 * pu, pour une panne, le plafond de l'app ou un refus du modele.
 */
export const ETATS_IA = ["a_ecrire", "ecrits", "echec", "plafond", "refus"] as const;
export type EtatIa = (typeof ETATS_IA)[number];
export const etatIaSchema = z.enum(ETATS_IA);

/** L'etat d'un post : propose a la validation, ou retire par Nicolas. */
export const ETATS_POST = ["propose", "retire"] as const;
export type EtatPost = (typeof ETATS_POST)[number];
export const etatPostSchema = z.enum(ETATS_POST);

/** L'etat d'une semaine : preparee (modifiable) ou validee (figee, ses envois sont crees). */
export const ETATS_SEMAINE = ["preparee", "validee"] as const;
export type EtatSemaine = (typeof ETATS_SEMAINE)[number];
export const etatSemaineSchema = z.enum(ETATS_SEMAINE);

/** Ce qu'un post publie : une video (avec son clip) ou un texte seul. */
export const TYPES_POST = ["video", "texte"] as const;
export type TypePost = (typeof TYPES_POST)[number];
export const typePostSchema = z.enum(TYPES_POST);

/**
 * Le texte d'un post pour un reseau : le titre n'existe que pour YouTube (`LONGUEURS`), le texte est la
 * description de YouTube ou le post des autres. Un texte vide est permis : Nicolas peut vider un champ, la
 * validation le compte alors « sans texte ».
 */
export const texteReseauSchema = z.object({ titre: z.string().optional(), texte: z.string() });

/**
 * Les textes d'un post, un par reseau, tous facultatifs : un post qui vient de naitre n'en a aucun. Un reseau
 * qui n'est pas dans la liste des sept est refuse. Les longueurs et le titre de YouTube seul sont jugees par le
 * domaine (`semaine.ts`), qui dit alors `texte_trop_long` ou `textes_invalides`.
 */
export const textesSchema = z.partialRecord(reseauSchema, texteReseauSchema);
export type TextesContrat = z.infer<typeof textesSchema>;

/** Le fichier du clip d'un post : son identifiant, et s'il est complet (sinon la regie dit « en attente du PC »). */
export const fichierDuPostSchema = z.object({ id: z.string(), complet: z.boolean() });

/**
 * Un envoi d'un post, vu de la semaine : son reseau et son etat, rien d'autre. Seuls les envois non retires y
 * figurent. L'ecran montre ainsi ce qui est parti ou programme, et ce qui manque apres une validation coupee ; le
 * suivi des envois (`reponseEnvoisSchema`) dit le reste.
 */
export const envoiDuPostSchema = z.object({ reseau: reseauSchema, etat: etatEnvoiSchema });

/**
 * Un post de la semaine, tel que la regie le lit.
 *
 * - `jour` : le jour de publication, AAAA-MM-JJ (heure de Paris) ;
 * - `clip` : la fiche du clip, `null` pour un post de texte ;
 * - `reseaux` : les reseaux coches, dans l'ordre que Nicolas leur a donne ;
 * - `textes`, `ia` : ce qui est ecrit, et ou en est l'IA ;
 * - `doublon` : le clip (ou un clip de meme empreinte) part deja d'un post non retire d'une autre semaine validee
 *   avant celle-ci : tant que la semaine en porte un, « Valider » ne cree rien ; Nicolas l'echange ou le retire ;
 * - `fichier` : le fichier du clip, `null` sans fichier ;
 * - `envois` : ses envois non retires, un par reseau au plus, dans l'ordre des sept reseaux. Vide tant que la
 *   semaine n'est pas validee.
 */
export const vuePostSchema = z.object({
  id: z.string(),
  jour: z.string(),
  type: typePostSchema,
  clip: clipStockSchema.nullable(),
  reseaux: z.array(reseauSchema),
  textes: textesSchema,
  ia: etatIaSchema,
  etat: etatPostSchema,
  doublon: z.boolean(),
  fichier: fichierDuPostSchema.nullable(),
  envois: z.array(envoiDuPostSchema),
});
export type VuePostContrat = z.infer<typeof vuePostSchema>;

/**
 * La semaine d'un lundi (`GET /api/diffusion/semaine`) : son etat, `null` quand elle n'existe pas (aucun post),
 * et ses posts dans l'ordre des jours, retires compris.
 */
export const vueSemaineSchema = z.object({
  lundi: z.string(),
  etat: etatSemaineSchema.nullable(),
  posts: z.array(vuePostSchema),
});
export type VueSemaineContrat = z.infer<typeof vueSemaineSchema>;

/**
 * Ce que « Preparer maintenant » rend. `nouvelle` n'est vrai que si cet appel a cree la semaine. Faux avec
 * `posts: 0` et des manquants veut dire « pas de semaine, faute de clip » ; faux avec des posts, que la semaine
 * existait deja.
 */
export const resultatPreparationSchema = z.object({
  posts: z.number().int().nonnegative(),
  manquants: z.number().int().nonnegative(),
  nouvelle: z.boolean(),
});
export type ResultatPreparationContrat = z.infer<typeof resultatPreparationSchema>;

/**
 * Ce que « Valider la semaine » rend.
 *
 * - `envois` : les envois non retires de la semaine apres l'appel, ceux d'avant compris ;
 * - `etat` : l'etat de la semaine apres l'appel. `preparee` quand il n'y avait rien a publier, ou quand un post est en
 *   doublon (rien n'est cree, pas meme pour les autres posts, tant que Nicolas ne l'a pas echange ou retire) : elle
 *   reste modifiable, et `envois` est a zero ;
 * - `nonBranches` : les reseaux coches qui n'ont pas pu recevoir d'envoi parce qu'ils ne sont pas branches ;
 * - `sansTexte` : les couples (post, reseau branche) auxquels il manque un texte : aucun envoi n'y est cree ;
 * - `doublons` : les posts dont le clip part deja d'une autre semaine validee : aucun envoi, et la semaine n'est pas
 *   figee tant qu'il en reste.
 */
export const resultatValidationSchema = z.object({
  envois: z.number().int().nonnegative(),
  etat: etatSemaineSchema,
  nonBranches: z.array(reseauSchema),
  sansTexte: z.number().int().nonnegative(),
  doublons: z.number().int().nonnegative(),
});
export type ResultatValidationContrat = z.infer<typeof resultatValidationSchema>;

/**
 * Ce que Nicolas change d'un post avant de valider (`PATCH /api/diffusion/posts/{id}`). Un champ absent ne change
 * rien ; une demande vide non plus.
 *
 * - `reseaux` remplace la liste des reseaux coches ;
 * - `textes` fusionne reseau par reseau : un reseau donne remplace le sien, les autres restent ;
 * - `retirer: true` retire le post ;
 * - `echangerContre` : l'identifiant d'un clip disponible, qui prend la place (ses textes sont alors a reecrire).
 *
 * Le schema ne juge que la forme. Une liste de reseaux vide ou en double, un texte trop long, un titre pour un
 * reseau qui n'en a pas et un clip qui n'est pas disponible sont refuses par le domaine, avec leur code.
 */
export const modificationPostSchema = z.object({
  reseaux: z.array(reseauSchema).optional(),
  textes: textesSchema.optional(),
  retirer: z.boolean().optional(),
  echangerContre: identifiantSchema.optional(),
});
export type ModificationPostContrat = z.infer<typeof modificationPostSchema>;

/**
 * Les comptes du stock (`GET /api/diffusion/stock`).
 *
 * - `total` : toutes les fiches du stock, absentes et publiees comprises ;
 * - `presents` : celles dont le fichier est dans le dossier du stock ;
 * - `publies` : celles qui sont dans un post non retire d'une semaine validee ;
 * - `restants` : les clips disponibles qu'une semaine a encore a prendre : sans ceux dont le jour prevu (`publicationPrevue`)
 *   est avant aujourd'hui, a Paris, que plus aucune semaine ne prendra (le clip est sorti ailleurs, et seul un echange a la
 *   main le reprend) ;
 * - `joursParSemaine` : le nombre de jours de diffusion regles ;
 * - `semaines` : combien de semaines entieres les clips restants font a ce rythme (`restants` divise par
 *   `joursParSemaine`, arrondi a l'entier inferieur).
 */
export const comptesStockSchema = z.object({
  total: z.number().int().nonnegative(),
  presents: z.number().int().nonnegative(),
  publies: z.number().int().nonnegative(),
  restants: z.number().int().nonnegative(),
  joursParSemaine: z.number().int().nonnegative(),
  semaines: z.number().int().nonnegative(),
});
export type ComptesStockContrat = z.infer<typeof comptesStockSchema>;

/** La reponse de `GET /api/diffusion/stock` : tout le stock, dans l'ordre des plus vus, et ses comptes. */
export const reponseStockSchema = z.object({
  clips: z.array(clipStockSchema),
  comptes: comptesStockSchema,
});
export type ReponseStockContrat = z.infer<typeof reponseStockSchema>;

/** Les etats d'une tache de l'agent, tels que la file les garde. */
export const ETATS_INVENTAIRE = ["a_faire", "en_cours", "faite", "echec"] as const;
export const etatInventaireSchema = z.enum(ETATS_INVENTAIRE);
export type EtatInventaire = z.infer<typeof etatInventaireSchema>;

/** La forme d'un code de panne : minuscules, chiffres et tirets bas. Tout autre texte n'est jamais rendu. */
export const FORME_CODE_PANNE = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * L'avancement de la derniere tache `inventaire` (`GET /api/diffusion/stock/inventaire`), ou `null` quand aucun
 * inventaire n'a jamais ete demande.
 *
 * - `etat` : `a_faire` (demande, pas encore prise), `en_cours` (l'agent lit le dossier), `faite`, `echec` ;
 * - `demandeLe` : la creation de la tache, date ISO 8601 (UTC) ;
 * - `finiLe` : la fin (faite ou en echec pour de bon), `null` sinon ;
 * - `panne` : le **code** de la panne que l'agent a notee (`accroches_illisibles`, `dossier_stock_absent`...), `null`
 *   sans panne. Jamais un message ni un chemin : un texte qui n'a pas la forme d'un code n'est pas rendu.
 */
export const inventaireEtatSchema = z
  .object({
    etat: etatInventaireSchema,
    demandeLe: z.string(),
    finiLe: z.string().nullable(),
    panne: z.string().regex(FORME_CODE_PANNE).nullable(),
  })
  .nullable();
export type InventaireEtatContrat = z.infer<typeof inventaireEtatSchema>;

/**
 * Un envoi du programme (`GET /api/diffusion/programme`) : tout ce qui va partir, ou est parti, un jour donne.
 *
 * - `origine` : `libre` (un post libre, `libre:<cle>:<reseau>`) ou `semaine` (un post de la semaine du stock) ;
 * - `sorte` : le type de l'envoi ;
 * - `modifiable` : vrai quand l'envoi est `prevu` et sans preparation chez le reseau (`PATCH` le corrige) ;
 * - `lienReseau` : le lien public une fois parti, ou l'endroit chez le reseau ou un envoi programme se change ;
 * - `noteReseau` : la phrase qui dit ou le changer, pour un envoi deja programme chez le reseau ;
 * - `fichierId` : le fichier de l'envoi (la video ou l'image), `null` tant qu'il n'est pas arrive : la regie en tire l'apercu.
 */
export const envoiProgrammeSchema = z.object({
  id: z.string(),
  cle: z.string(),
  origine: z.enum(["libre", "semaine"]),
  reseau: reseauSchema,
  sorte: z.enum(["video", "image", "texte"]),
  moment: z.string(),
  etat: etatEnvoiSchema,
  titre: z.string().nullable(),
  texte: z.string(),
  modifiable: z.boolean(),
  lienReseau: z.string().nullable(),
  noteReseau: z.string().nullable(),
  fichierId: z.string().nullable(),
});
export type EnvoiProgramme = z.infer<typeof envoiProgrammeSchema>;

/** Un jour de Paris (AAAA-MM-JJ) et ses envois, du plus tot au plus tard puis par reseau. */
export const programmeJourSchema = z.object({
  jour: z.string(),
  envois: z.array(envoiProgrammeSchema),
});
export type ProgrammeJour = z.infer<typeof programmeJourSchema>;

/** Sept jours de Paris de `depuis` a `jusqua` compris, chacun present meme vide. */
export const programmeSchema = z.object({
  depuis: z.string(),
  jusqua: z.string(),
  jours: z.array(programmeJourSchema),
});
export type Programme = z.infer<typeof programmeSchema>;

/** Ce que `PATCH /api/diffusion/envois/{id}` change d'un envoi prevu : au moins un champ. */
export const correctionEnvoiSchema = z
  .object({
    texte: z.string().max(5000).optional(),
    titre: z.string().max(100).nullable().optional(),
    moment: z.string().datetime({ offset: true }).optional(),
  })
  .refine((c) => Object.keys(c).length > 0, { message: "Au moins un champ a changer." });
export type CorrectionEnvoi = z.infer<typeof correctionEnvoiSchema>;
